import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../../../store/useAuthStore";
import { MdSearch } from "react-icons/md";
import { BsPlusCircle } from "react-icons/bs";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TablePagination, Skeleton,
} from "@mui/material";

const InventoryCatalog = () => {
  const { token } = useAuthStore();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const fetchItems = async () => {
    setLoading(true);
    try {
      let url = `/api/getAllInventoryItems?page=${page + 1}&limit=${rowsPerPage}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setItems(data.items || []);
        setTotalCount(data.totalCount || 0);
      }
    } catch (err) {
      console.error("Error fetching catalog:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [page, rowsPerPage, search]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(0);
  };

  const getStockBadge = (item) => {
    if (item.currentStock <= 0) {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">Out of Stock</span>;
    }
    if (item.minThreshold > 0 && item.currentStock <= item.minThreshold) {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">Low Stock ({item.currentStock})</span>;
    }
    return <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">In Stock ({item.currentStock})</span>;
  };

  const handleRequestItem = (item) => {
    navigate(`/Requisitions/New?itemId=${item._id}&itemName=${encodeURIComponent(item.name)}`);
  };

  return (
    <div className="m-2 md:m-6 p-2 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">Inventory Catalog</h2>
          <p className="text-sm text-gray-500 mt-1">Browse available items and submit a requisition</p>
        </div>
        <button
          onClick={() => navigate("/Requisitions/New")}
          className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90"
        >
          <BsPlusCircle />
          New Requisition
        </button>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search items by name or SKU..."
            className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <button
          type="submit"
          className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-sm hover:bg-gray-200"
        >
          Search
        </button>
        {search && (
          <button
            type="button"
            onClick={() => { setSearch(""); setSearchInput(""); setPage(0); }}
            className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700"
          >
            Clear
          </button>
        )}
      </form>

      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                <TableCell sx={{ fontWeight: 600 }}>Item Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>SKU</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Unit Cost (Nu)</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Stock Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(7)].map((_, j) => (
                      <TableCell key={j}><Skeleton animation="wave" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <p className="text-gray-400">No items found</p>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow key={item._id} hover>
                    <TableCell>
                      <div>
                        <p className="font-medium text-gray-800">{item.name}</p>
                        {item.description && (
                          <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[200px]">{item.description}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs font-mono text-gray-500">{item.sku}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-600">{item.category?.name || "—"}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-600">{item.unitOfMeasure || "—"}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm font-medium">Nu. {item.unitCost?.toLocaleString() || "0"}</span>
                    </TableCell>
                    <TableCell>{getStockBadge(item)}</TableCell>
                    <TableCell>
                      <button
                        onClick={() => handleRequestItem(item)}
                        className="flex items-center gap-1 text-xs text-primary border border-primary rounded-lg px-3 py-1.5 hover:bg-primary hover:text-white transition-colors"
                      >
                        <BsPlusCircle className="text-xs" />
                        Request
                      </button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>
    </div>
  );
};

export default InventoryCatalog;
